import axios from 'axios'
import { useState, useContext } from 'react'
import Head from 'next/head'
import clsx from 'clsx'
import Link from 'next/link'
import {
  IonSend,
  IcRoundLocalPhone,
  MdiMapMarker,
  MaterialSymbolsMailRounded,
} from '../data/svgIcons/all-svg-icons'
import { LanguageContext } from '../hook/context'
import { CorporateContactJsonLd } from 'next-seo'

const Contact = () => {
  const { t } = useContext(LanguageContext)

  const [isSent, setIsSent] = useState('initial')

  const [inputs, setInputs] = useState({
    email: '',
    subject: '',
    message: '',
    phone: '',
    firstName: '',
    lastName: '',
  })

  const handleOnChange = (event: any) => {
    event.persist()
    setInputs(prev => ({
      ...prev,
      [event.target.id]: event.target.value,
    }))
  }

  const handleOnSubmit = (event: any) => {
    event.preventDefault()

    axios({
      method: 'POST',
      url: 'https://formbold.com/s/94aW3',
      data: inputs,
    })
      .then(r => {
        setIsSent('success')
      })
      .catch(r => {
        setIsSent('error')
      })

    setInputs({
      email: '',
      subject: '',
      message: '',
      phone: '',
      firstName: '',
      lastName: '',
    })
  }

  const messageAlert = (x: string) => {
    switch (x) {
      case 'success':
        return (
          <p className='sm:mr-10 sm:mb-0 mb-5 font-bold text-emerald-500'>
            {t.contact.success}
          </p>
        )
      case 'error':
        return (
          <p className='sm:mr-10 sm:mb-0 mb-5 font-bold text-red-500 w-4/5'>
            {t.contact.error}
          </p>
        )
      default:
        break
    }
  }

  return (
    <section className='my-14 text-neutral-400'>
      <Head>
        <title>
          {t.contact.title} | {t.seo.title}
        </title>
      </Head>
      <CorporateContactJsonLd
        url='https://www.colorwayxpress.com'
        contactPoint={[
          {
            telephone: '+90 850 622 33 83',
            contactType: 'Technical Support',
            areaServed: 'TR',
            availableLanguage: 'Turkish',
          },
          {
            email: 'info@docode.com.tr',
            contactType: 'Technical Support',
            areaServed: 'TR',
            availableLanguage: ['Turkish', 'English'],
          },
        ]}
      />
      <div className='lg:col-span-2  flex justify-between flex-col lg:flex-row'>
        {/* Contact form */}
        <div className='flex-1 max-w-2xl'>
          <h1 className='text-2xl tracking-tighter font-bold text-neutral-200'>
            {t.contact.title}
          </h1>
          {t.contact.description}
          <form
            onSubmit={handleOnSubmit}
            className='mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'
          >
            <div className='sm:col-span-2'>
              <div className='mt-1'>
                <label htmlFor='subject' className='block text-sm font-medium '>
                  {t.contact.subject}
                  <span className='text-red-600'>*</span>
                </label>

                <input
                  onChange={handleOnChange}
                  value={inputs.subject}
                  required
                  id='subject'
                  type='text'
                  name='subject'
                  className='block w-full rounded-md bg-neutral-800 border-warm-gray-300 py-3 px-4  shadow-sm'
                />
              </div>
            </div>
            <div>
              <label
                htmlFor='first-name'
                className='block text-sm font-medium '
              >
                {t.contact.name}
                <span className='text-red-600'>*</span>
              </label>
              <div className='mt-1'>
                <input
                  type='text'
                  name='firstName'
                  id='firstName'
                  pattern='[A-Za-z ]+'
                  required
                  value={inputs.firstName}
                  onChange={handleOnChange}
                  autoComplete='given-name'
                  className='block w-full bg-neutral-800 rounded-md border-warm-gray-300 py-3 px-4  shadow-sm '
                />
              </div>
            </div>
            <div>
              <label htmlFor='last-name' className='block text-sm font-medium '>
                {t.contact.lastName}
                <span className='text-red-600'>*</span>
              </label>
              <div className='mt-1'>
                <input
                  type='text'
                  name='lastName'
                  id='lastName'
                  required
                  pattern='[A-Za-z ]+'
                  value={inputs.lastName}
                  onChange={handleOnChange}
                  autoComplete='family-name'
                  className='block w-full bg-neutral-800 rounded-md border-warm-gray-300 py-3 px-4  shadow-sm'
                />
              </div>
            </div>
            <div>
              <label htmlFor='email' className='block text-sm font-medium '>
                Email<span className='text-red-600'>*</span>
              </label>
              <div className='mt-1'>
                <input
                  id='email'
                  name='email'
                  type='email'
                  required
                  value={inputs.email}
                  onChange={handleOnChange}
                  autoComplete='email'
                  className='block w-full rounded-md bg-neutral-800 border-warm-gray-300 py-3 px-4  shadow-sm'
                />
              </div>
            </div>
            <div>
              <div className='flex justify-between'>
                <label htmlFor='phone' className='block text-sm font-medium '>
                  {t.contact.phone}
                </label>
                <span id='phone' className='text-sm'>
                  {t.contact.phoneOptional}
                </span>
              </div>
              <div className='mt-1'>
                <input
                  type='text'
                  name='phone'
                  id='phone'
                  value={inputs.phone}
                  onChange={handleOnChange}
                  autoComplete='tel'
                  className='block w-full rounded-md bg-neutral-800 border-warm-gray-300 py-3 px-4  shadow-sm'
                  aria-describedby='phone-optional'
                />
              </div>
            </div>
            <div className='sm:col-span-2'>
              <label htmlFor='message' className='block text-sm font-medium '>
                {t.contact.message}
                <span className='text-red-600'>*</span>
              </label>

              <div className='mt-1'>
                <textarea
                  id='message'
                  name='message'
                  required
                  value={inputs.message}
                  onChange={handleOnChange}
                  rows={4}
                  className='block w-full rounded-md bg-neutral-800 border-warm-gray-300 py-3 px-4 shadow-sm'
                  aria-describedby='message-max'
                />
              </div>
            </div>
            <div className='sm:col-span-2 flex transition-all sm:justify-end sm:items-center sm:flex-row flex-col items-center text-center'>
              {messageAlert(isSent)}

              <button
                type='submit'
                className={clsx(
                  isSent === 'success'
                    ? 'bg-emerald-600 hover:bg-emerald-800'
                    : isSent === 'error'
                    ? 'bg-red-600 hover:bg-red-800'
                    : 'bg-blue-600 hover:bg-blue-800',
                  'inline-flex w-full items-center justify-center  overflow-hidden transition-all duration-500 rounded-md border border-transparent  px-6 py-2 text-base font-medium text-white shadow-sm  sm:w-auto',
                )}
              >
                {t.contact.submit} <IonSend className='ml-3' />
              </button>
            </div>
          </form>
        </div>
        <div className='lg:ml-10 mt-10 lg:mt-[133px]'>
          <div className='bg-neutral-50/0 lg:bg-neutral-800 lg:divide-y-0 divide-y divide-neutral-500/50 lg:p-3 rounded-lg'>
            <div className='flex py-2 hover:bg-neutral-500/10 p-3 transition-all lg:rounded-xl'>
              <MdiMapMarker className='mr-2' width={25} height={25} />

              <Link
                href='https://goo.gl/maps/qpVKbWG9EZeinNFT6'
                target={'_blank'}
              >
                <p>DoCode Bilgisayar ve Yazılım San. Tic. Ltd. Şti.</p>
                <p>Üniversite-1. Cd. ULUTEK TGB. Ofis: 119, No: 933</p>
                <p>Nilüfer / Bursa / Turkey</p>
              </Link>
            </div>
            <div className='flex hover:bg-neutral-500/10 p-3 transition-all lg:rounded-xl'>
              <IcRoundLocalPhone width={25} height={25} className='mr-2' />

              <Link href='tel:+90 (850) 622 33 83'>
                <p>+90 (850) 622 33 83</p>
              </Link>
            </div>
            <div className='flex hover:bg-neutral-500/10 p-3 transition-all lg:rounded-xl'>
              <MaterialSymbolsMailRounded
                className='mr-2'
                width={25}
                height={25}
              />
              <Link href='mailto:info@docode.com.tr'>
                <p>info [at] docode.com.tr</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
